import { Injectable } from '@angular/core';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Order } from '../../dto/order.dto';
import { OrderChangesService } from './order-changes.service';
import { OrdersService } from './orders.service';
import { OrdersMergeService } from './orders-merge.service';

export interface OrderProviderServiceContext {
  loading: boolean;
  targetUrl: string;
  order: Order;
  orderId: number;
}

/**
 * Поставщик заказа для экранов заявок. Заказ берётся из двух источников с приоритетом локального:
 * 1) реактивный поток последнего состояния заказа ({@link OrderChangesService}) — актуальная
 *    локальная версия, которую поддерживают правки пользователя и приход синхронизации с сервера;
 * 2) загрузка с сервера — только если локального состояния ещё нет (первое открытие заявки).
 *
 * Загруженный с сервера заказ ПЕРЕД показом сливается с локальными несинхронизированными правками
 * ({@link OrdersMergeService}) и кладётся обратно в поток, чтобы дальше все подписчики читали единую
 * версию, а не «сырую» серверную. Отсюда же следует, что при повторном открытии экрана виден результат
 * последнего локального сохранения (это опора идемпотентности номера ТТН и подобной логики).
 */
@Injectable({
  providedIn: 'root'
})
export class OrderProviderService {
  constructor(
    private readonly orderChangesService: OrderChangesService,
    private readonly ordersService: OrdersService,
    private readonly ordersMergeService: OrdersMergeService
  ) {}

  /**
   * Отдаёт заказ на экран через реактивный поток. Пока по заказу есть локальное состояние (в потоке или
   * уже положенное в {@code context.order}), сервер не трогаем — показываем локальную версию; иначе один
   * раз подгружаем с сервера ({@link #fetchOrder}). Возвращает {@code true} ТОЛЬКО когда пришёл НОВЫЙ
   * экземпляр заказа (отличный от текущего в контексте) — это сигнал компоненту обновиться; повтор той же
   * ссылки даёт {@code false}, чтобы не перерисовывать зря. Поток живёт, пока подписан компонент, поэтому
   * последующие правки/синхронизация до́лжным образом дотекают до экрана тем же каналом.
   */
  public loadOrder(context: OrderProviderServiceContext): Observable<boolean> {
    context.loading = true;
    return this.orderChangesService.getOrderSubject(context.targetUrl).pipe(
      // Приоритет локального: сервер запрашиваем лишь когда состояния нет ни в потоке, ни в контексте.
      switchMap(order =>
        !order && !context.order ? this.fetchOrder(context) : of(order)
      ),
      // Публикуем версию в контекст и сигналим обновление только при СМЕНЕ ссылки на заказ.
      map(order => {
        if (order != null && order !== context.order) {
          context.order = order;
          context.loading = false;
          return true;
        }
        return false;
      })
    );
  }

  /**
   * Разовая загрузка заказа с сервера с последующим слиянием. Порядок важен: сервер → слияние с
   * локальными несинхронизированными правками ({@link OrdersMergeService#mergeOrder}) → запись
   * результата в реактивный поток ({@code updateRequests}), откуда его подхватят все подписчики. Без
   * {@code orderId} (например, прямой заход по ссылке) загружать нечего — отдаём {@code null}.
   */
  private fetchOrder(
    context: OrderProviderServiceContext
  ): Observable<Order | null> {
    // Нет идентификатора заказа — загружать нечего.
    if (!context.orderId) {
      return of(null);
    }
    return this.ordersService.getOrderById(context.orderId).pipe(
      map(response => response?.data as Order),
      // Слияние возвращает промис — оборачиваем в поток; отсутствующий заказ (null) пропускаем без слияния.
      switchMap(order =>
        order == null
          ? of(order)
          : from(this.ordersMergeService.mergeOrder(order))
      ),
      // Слитую версию кладём в реактивный поток — дальше экраны читают её, а не серверную «сырую».
      tap(order => {
        if (order != null) {
          this.orderChangesService.updateRequests(order, context.targetUrl);
        }
      })
    );
  }
}
